import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, IconButton, Menu, MenuItem, Typography, Box, Avatar, useTheme, useMediaQuery, Tooltip, Chip, ListItemText } from '@mui/material';
import { Menu as MenuIcon, Search as SearchIcon } from '@mui/icons-material';
import { canManageSistemaUsers, effectiveUserRoles } from '../utils/sistemaAuthz';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getSiteName } from '../config/appConfig';
import ChangePasswordDialog from './ChangePasswordDialog';
import BuscadorAccesosModal from './BuscadorAccesosModal';
import PageHelpButton from './help/PageHelpButton';

interface HeaderProps {
  onMenuClick?: () => void;
  desktopOpen?: boolean;
  onDesktopToggle?: () => void;
  /** Drawer de ayuda abierto (Layout reserva padding al contenido). */
  pageHelpOpen?: boolean;
  onPageHelpOpenChange?: (open: boolean) => void;
}

const Header: React.FC<HeaderProps> = ({
  onMenuClick,
  desktopOpen = true,
  onDesktopToggle,
  pageHelpOpen,
  onPageHelpOpenChange,
}) => {
  const navigate = useNavigate();
  const { user, logout, tenantName, tenants, switchTenant } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [buscadorOpen, setBuscadorOpen] = useState(false);
  const [tenantChipEl, setTenantChipEl] = useState<null | HTMLElement>(null);
  const [apkDownloading, setApkDownloading] = useState(false);
  const apkDownloadUrl = 'https://oneapp.neos.com.ar/assets/eone-app.apk';
  const apkFileName = 'eone-app.apk';
  const puedeAdministrar = canManageSistemaUsers(effectiveUserRoles(user?.roles));

  const handleSwitchTenant = async (id: number) => {
    setTenantChipEl(null);
    setAnchorEl(null);
    await switchTenant(id);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        setBuscadorOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
    navigate('/login');
  };

  const handleChangePassword = () => {
    handleClose();
    setChangePasswordOpen(true);
  };

  const handleEditMyProfile = () => {
    handleClose();
    if (user?.username) {
      navigate(`/sistema/usuarios/${encodeURIComponent(user.username)}`);
    }
  };

  const handleDownloadApk = async () => {
    handleClose();
    if (apkDownloading) {
      return;
    }

    setApkDownloading(true);
    try {
      const response = await fetch(apkDownloadUrl, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const contentType = response.headers.get('content-type') ?? '';
      if (contentType.includes('text/html')) {
        throw new Error('El servidor devolvió HTML en lugar del APK');
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = apkFileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(apkDownloadUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setApkDownloading(false);
    }
  };

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: '#ffffff',
        color: '#000000',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)'
      }}
    >
      <Toolbar>
        {isMobile && (
          <Tooltip title="Abrir menú">
          <IconButton
            color="inherit"
            aria-label="abrir menú"
            edge="start"
            onClick={onMenuClick}
              sx={{ 
                mr: 2, 
                color: '#00548f',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.05)'
                }
              }}
          >
            <MenuIcon />
          </IconButton>
          </Tooltip>
        )}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Box
            component={Link}
            to="/menu-principal"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              textDecoration: 'none'
            }}
          >
            <Box
              component="img"
              src="/assets/eone-logo.png"
              alt="eOne"
              sx={{
                height: { xs: 52, sm: 56, md: 64 },
                width: 'auto'
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              letterSpacing: '0.02em',
              fontFamily: [
                '-apple-system',
                'BlinkMacSystemFont',
                '"Segoe UI"',
                'Roboto',
                '"Helvetica Neue"',
                'Arial',
                'sans-serif',
              ].join(','),
              display: { xs: 'none', sm: 'block' },
              fontSize: '1rem',
              mr: 2
            }}
          >
            {getSiteName()}
          </Typography>
          {tenantName && (
            <>
              <Chip
                label={tenantName}
                size="small"
                color="primary"
                variant="outlined"
                onClick={(e) => tenants.length > 1 && setTenantChipEl(e.currentTarget)}
                sx={{
                  mr: 2,
                  fontWeight: 500,
                  cursor: tenants.length > 1 ? 'pointer' : 'default'
                }}
              />
              {tenants.length > 1 && (
                <Menu
                  anchorEl={tenantChipEl}
                  open={Boolean(tenantChipEl)}
                  onClose={() => setTenantChipEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  {tenants.map((t) => (
                    <MenuItem
                      key={t.id}
                      onClick={async () => {
                        setTenantChipEl(null);
                        await handleSwitchTenant(t.id);
                      }}
                    >
                      <Typography>{t.name}</Typography>
                    </MenuItem>
                  ))}
                </Menu>
              )}
            </>
          )}
          <Tooltip title="Buscar accesos (Ctrl+K)">
            <IconButton
              onClick={() => setBuscadorOpen(true)}
              sx={{ 
                color: '#00548f',
                '&:hover': {
                  backgroundColor: 'rgba(0, 84, 143, 0.08)'
                }
              }}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>
          <IconButton
            onClick={handleMenu}
            sx={{ 
              padding: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)'
              }
            }}
          >
            <Avatar 
              sx={{ 
                bgcolor: '#1976d2',
                color: 'white',
                width: 35,
                height: 35
              }}
            >
              {user?.username?.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <PageHelpButton open={pageHelpOpen} onOpenChange={onPageHelpOpenChange} />
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem onClick={handleEditMyProfile} disabled={!user?.username}>
              <ListItemText
                primary={user?.displayName || user?.username || '—'}
                secondary="Mis datos"
                primaryTypographyProps={{ fontWeight: 600 }}
              />
            </MenuItem>
            <MenuItem onClick={handleDownloadApk} disabled={apkDownloading}>
              <Typography>
                {apkDownloading ? 'Descargando App Vendedores...' : 'Descargar App Vendedores'}
              </Typography>
            </MenuItem>
            {puedeAdministrar && (
              <MenuItem
                onClick={() => {
                  handleClose();
                  navigate('/configuraciones');
                }}
              >
                <ListItemText
                  primary="Configuración de la empresa"
                  secondary="Usuarios, maestros y correo"
                />
              </MenuItem>
            )}
            <MenuItem onClick={handleChangePassword}>
              <Typography>Cambiar Contraseña</Typography>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <Typography>Cerrar Sesión</Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
      <ChangePasswordDialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
      <BuscadorAccesosModal open={buscadorOpen} onClose={() => setBuscadorOpen(false)} />
    </AppBar>
  );
};

export default Header; 